import React, { useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { InvoiceMascot } from '../invoice-tour/invoice-mascot.component';
import './first-time-tour-prompt.scss';

const clampMascot = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);

/* FirstTimeTourPrompt
   --------------------
   A small centered modal shown ONCE on a user's very first visit to a
   page that hosts a guided tour. Two views:

     1. "ask"     — "Is this your first time? Take a quick guided tour."
                    Buttons: [Skip for Now] [Start Tour]
     2. "skipped" — only reached by clicking Skip for Now in view 1.
                    Reassures the user with a message + points them to
                    the Start Tour button in the page header.
                    Button: [Got it]

   The user MUST pick one of the dedicated action buttons to advance
   or dismiss — backdrop click, × button, and Escape key are all
   intentionally disabled so the user can't accidentally lose the
   prompt without committing to an action. Once dismissed via Got it /
   Start Tour, a "seen" flag is persisted to localStorage so the
   prompt never auto-shows again.

   `onSkip` fires when the user picks Skip for Now (transition to
   view 2). `onSkipDismiss` fires when the skip-confirmation view is
   closed via Got it. Together they let the parent pulse the Start
   Tour button for as long as the skip-confirmation view is open. */
interface FirstTimeTourPromptProps {
  /** Unique key for localStorage, e.g. `tour-prompt-seen:timesheet:user-12`. */
  storageKey: string;
  /** Called when the user clicks Start Tour in view 1. The parent
   *  then calls its tour hook's start() method. */
  onStart: () => void;
  /** Optional — called when the user picks Skip for Now in view 1.
   *  Parent typically starts pulsing the Start Tour button. */
  onSkip?: () => void;
  /** Optional — called when the skip-confirmation view is dismissed
   *  (via Got it). Parent stops the pulse. */
  onSkipDismiss?: () => void;

  /*
    Per-feature copy overrides. When omitted, the prompt falls back
    to the shared `TOUR_TS_*` translation keys (Timesheet copy) so
    every existing call site — currently just timeSheetsPage — keeps
    rendering identically. Every OTHER feature page (Manage Invoice,
    Drafts, Deductions, etc.) should pass its own strings so users
    see the correct feature name in the "Welcome to <feature>" title
    and body. Only the two ask-view strings vary by feature; the
    skip-view strings ("No problem!" / "Got it") are generic and
    default-driven for everyone.
  */
  /** View 1 title. Defaults to t('TOUR_TS_FIRST_TIME_TITLE'). */
  askTitle?: string;
  /** View 1 body. Defaults to t('TOUR_TS_FIRST_TIME_BODY'). */
  askBody?: string;
  /** View 1 Skip button label. Defaults to t('TOUR_TS_FIRST_TIME_SKIP'). */
  askSkipLabel?: string;
  /** View 1 Start button label. Defaults to t('TOUR_TS_FIRST_TIME_START'). */
  askStartLabel?: string;
  /** View 2 title. Defaults to t('TOUR_TS_PROMPT_SKIP_TITLE'). */
  skipTitle?: string;
  /** View 2 body. Defaults to t('TOUR_TS_PROMPT_SKIP_BODY'). */
  skipBody?: string;
  /** View 2 Got-it button label. Defaults to t('TOUR_TS_PROMPT_SKIP_OK'). */
  skipOkLabel?: string;

  /** When true, render the animated Invozio tour-guide mascot instead of the
   *  plain 👋 / 👍 emoji (same character used by the Create-Invoice tour).
   *  Opt-in so every existing call site keeps its emoji unchanged. */
  mascot?: boolean;
  /** Speech-bubble text shown when the user hovers Start Tour (mascot mode).
   *  Defaults to a short "Let's go!". */
  mascotBubble?: string;
}

type View = 'ask' | 'skipped';

export const FirstTimeTourPrompt: React.FC<FirstTimeTourPromptProps> = ({
  storageKey,
  onStart,
  onSkip,
  onSkipDismiss,
  askTitle,
  askBody,
  askSkipLabel,
  askStartLabel,
  skipTitle,
  skipBody,
  skipOkLabel,
  mascot = false,
  mascotBubble,
}) => {
  const { t } = useTranslation();

  // Mascot interactivity — eyes follow the cursor, expression reacts to the
  // action buttons (happy on Start, sad on Skip/Got it). Only active when the
  // `mascot` prop is set; harmless no-ops otherwise (eyesRef stays null).
  const eyesRef = useRef<SVGGElement | null>(null);
  const mascotRef = useRef<HTMLDivElement | null>(null);
  const [hoverStart, setHoverStart] = useState(false);
  const [hoverSad, setHoverSad] = useState(false);
  const mascotExpression = hoverSad ? 'sad' : hoverStart ? 'happy' : 'idle';

  const handleTrackEyes = (e: React.MouseEvent): void => {
    const m = mascotRef.current?.getBoundingClientRect();
    if (!m || !eyesRef.current) return;
    const ox = m.left + m.width / 2;
    const oy = m.top + m.height * 0.42;
    const dx = clampMascot((e.clientX - ox) / 220, -1, 1);
    const dy = clampMascot((e.clientY - oy) / 220, -1, 1);
    eyesRef.current.style.transform = `translate(${dx * 2.6}px, ${dy * 2.2}px)`;
  };
  const resetEyes = (): void => {
    if (eyesRef.current) eyesRef.current.style.transform = 'translate(0px, 0px)';
  };

  /* Resolve every visible string with the passed override taking
     precedence over the default TS translation key. `?? (t(...) as
     string)` means "use the override if the parent passed one, else
     fall back to the shared Timesheet key" — so existing Timesheet
     usage (which passes none of these props) is byte-identical. */
  const askTitleText = askTitle ?? (t('TOUR_TS_FIRST_TIME_TITLE') as string);
  const askBodyText = askBody ?? (t('TOUR_TS_FIRST_TIME_BODY') as string);
  const askSkipText = askSkipLabel ?? (t('TOUR_TS_FIRST_TIME_SKIP') as string);
  const askStartText = askStartLabel ?? (t('TOUR_TS_FIRST_TIME_START') as string);
  const skipTitleText = skipTitle ?? (t('TOUR_TS_PROMPT_SKIP_TITLE') as string);
  const skipBodyText = skipBody ?? (t('TOUR_TS_PROMPT_SKIP_BODY') as string);
  const skipOkText = skipOkLabel ?? (t('TOUR_TS_PROMPT_SKIP_OK') as string);

  /* Visibility is now controlled by the PARENT via mount/unmount
     (server-driven — see timeSheetsPage.tsx's `isTimesheetTourSkip`
     guard). We therefore initialise `visible` to `true` unconditionally
     — the component only mounts when the parent has already decided
     the user should see the prompt. The legacy localStorage-based
     read was blocking display for users whose browser still had the
     old "seen" flag from a prior session even though the server said
     they should be prompted again.
     `dismiss()` still writes localStorage (harmless legacy
     safety-net) but that write no longer gates first-render display. */
  const [visible, setVisible] = useState<boolean>(true);
  const [view, setView] = useState<View>('ask');

  const dismiss = (): void => {
    setVisible(false);
    if (typeof window !== 'undefined') {
      try { window.localStorage.setItem(storageKey, 'true'); } catch { /* ignore */ }
    }
  };

  if (!visible) return null;
  if (typeof document === 'undefined') return null;

  const handleStart = (): void => {
    dismiss();
    onStart();
  };

  /* Skip for Now — DOESN'T dismiss; switches to the skip-confirmation
     view AND notifies the parent so it can start pulsing the Start
     Tour button. The user dismisses from view 2 via Got it. */
  const handleSkip = (): void => {
    setView('skipped');
    onSkip?.();
  };

  /* Got it — dismisses from the skip-confirmation view. Tells the
     parent the pulse can stop. */
  const handleGotIt = (): void => {
    dismiss();
    onSkipDismiss?.();
  };

  return ReactDOM.createPortal(
    <div className="fttp-overlay" role="presentation" aria-hidden="false">
      {/*
        Backdrop click is intentionally NOT wired — the only exits are
        the dedicated action buttons inside the card. This is a "must
        commit" onboarding pattern: the user picks Skip or Start in
        view 1, and Got it in view 2. No × icon, no Escape, no
        backdrop dismissal — so the prompt can't be silently lost.
      */}
      <div
        className={`fttp-card${mascot ? ' fttp-card--mascot' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="fttp-title"
        onMouseMove={mascot ? handleTrackEyes : undefined}
        onMouseLeave={mascot ? resetEyes : undefined}
      >
        {view === 'ask' ? (
          <>
            {mascot ? (
              <div className={`fttp-mascot${hoverStart ? ' is-happy' : ''}${hoverSad ? ' is-sad' : ''}`} ref={mascotRef} aria-hidden="true">
                <span className="fttp-mascot__halo" />
                {hoverStart && <span className="fttp-mascot__bubble">{mascotBubble ?? t('WELCOME_TOUR_LETSGO', { defaultValue: "Let's go!" })}</span>}
                <InvoiceMascot expression={mascotExpression} eyesRef={eyesRef} />
              </div>
            ) : (
              <div className="fttp-card__emoji" aria-hidden="true">👋</div>
            )}
            <h2 id="fttp-title" className="fttp-card__title">{askTitleText}</h2>
            <p className="fttp-card__body">{askBodyText}</p>

            <div className="fttp-card__actions">
              <button
                type="button"
                className="fttp-btn fttp-btn--ghost"
                onClick={handleSkip}
                onMouseEnter={mascot ? () => setHoverSad(true) : undefined}
                onMouseLeave={mascot ? () => setHoverSad(false) : undefined}
              >
                {askSkipText}
              </button>
              <button
                type="button"
                className="fttp-btn fttp-btn--primary"
                onClick={handleStart}
                onMouseEnter={mascot ? () => setHoverStart(true) : undefined}
                onMouseLeave={mascot ? () => setHoverStart(false) : undefined}
              >
                {askStartText}
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Skip-confirmation view. The page is simultaneously
                pulsing the Start Tour button (triggered by onSkip and
                continued until onSkipDismiss fires), so this message
                + the pulse together teach the user where to come back. */}
            {mascot ? (
              <div className="fttp-mascot" ref={mascotRef} aria-hidden="true">
                <span className="fttp-mascot__halo" />
                <InvoiceMascot expression="idle" eyesRef={eyesRef} />
              </div>
            ) : (
              <div className="fttp-card__emoji" aria-hidden="true">👍</div>
            )}
            <h2 id="fttp-title" className="fttp-card__title">{skipTitleText}</h2>
            <p className="fttp-card__body">{skipBodyText}</p>

            <div className="fttp-card__actions">
              <button type="button" className="fttp-btn fttp-btn--primary" onClick={handleGotIt}>
                {skipOkText}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};
